import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { PickerMap } from '../maps/OsmMap';
import { Search, Loader2, MapPin, CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Address search (submit-based, Nominatim policy compliant) + draggable map pin
 * + delivery-radius validation. Calls onResolve({ address, geo, within }) on change.
 */
export const AddressMapPicker = ({ restaurantId, config, onResolve }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pin, setPin] = useState(null);          // { lat, lng }
  const [address, setAddress] = useState('');
  const [within, setWithin] = useState(null);     // null | true | false
  const [distance, setDistance] = useState(null);
  // Map starts on the customer's current city; if geolocation is unavailable we
  // fall back to Alicante (Spain). Resolved before the map mounts.
  const ALICANTE = { lat: 38.3452, lng: -0.481 };
  const [geoCenter, setGeoCenter] = useState(ALICANTE);
  const [geoReady, setGeoReady] = useState(false);
  const skipNext = useRef(false);

  useEffect(() => {
    let done = false;
    const finish = (c) => { if (!done) { done = true; if (c) setGeoCenter(c); setGeoReady(true); } };
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => finish(null),
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 600000 }
      );
      // Safety fallback if the permission prompt is ignored.
      setTimeout(() => finish(null), 7000);
    } else {
      finish(null);
    }
  }, []);

  const restGeo = config?.restaurant_geo;
  const radiusM = (config?.radius_km || 5) * 1000;
  const center = pin || geoCenter;

  const validate = useCallback(async (lat, lng) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/offsite/validate-radius/${restaurantId}`, { lat, lng });
      setWithin(data.within);
      setDistance(data.distance_m);
      return data.within;
    } catch (e) { setWithin(null); return null; }
  }, [restaurantId]);

  const applyPoint = useCallback(async (lat, lng, label) => {
    setPin({ lat, lng });
    let addr = label;
    if (!addr) {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/geocode/reverse`, { params: { lat, lon: lng } });
        addr = data.display_name || '';
      } catch (e) { addr = ''; }
    }
    setAddress(addr);
    const ok = await validate(lat, lng);
    onResolve && onResolve({ address: addr, geo: { lat, lng }, within: ok });
  }, [validate, onResolve]);

  const runSearch = useCallback(async (text, auto = false) => {
    const qv = (text || '').trim();
    if (qv.length < 3) { setSuggestions([]); return; }
    setSearching(true);
    if (!auto) setSuggestions([]);
    try {
      const bias = pin || geoCenter;
      const params = { q: qv, country: 'eu' };
      if (bias && bias.lat != null) { params.lat = bias.lat; params.lon = bias.lng; }
      const { data } = await axios.get(`${BACKEND_URL}/api/geocode/search`, { params });
      setSuggestions(data.results || []);
      // On an explicit submit with a single hit, place it directly.
      if (!auto && (data.results || []).length === 1) {
        const r = data.results[0];
        await applyPoint(r.lat, r.lon, r.short_name || r.display_name);
        setSuggestions([]);
      }
    } catch (e) { setSuggestions([]); } finally { setSearching(false); }
  }, [pin, geoCenter, applyPoint]);

  const search = (e) => { e?.preventDefault(); skipNext.current = false; runSearch(query, false); };

  // Keep latest runSearch in a ref so the debounce timer is never cleared by
  // identity changes (which would prevent the request from firing).
  const runSearchRef = useRef(runSearch);
  useEffect(() => { runSearchRef.current = runSearch; }, [runSearch]);

  // Live autocomplete: debounce typing and show suggestions automatically.
  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return undefined; }
    if (query.trim().length < 3) { setSuggestions([]); return undefined; }
    const id = setTimeout(() => runSearchRef.current(query, true), 400);
    return () => clearTimeout(id);
  }, [query]);

  const pickSuggestion = async (r) => {
    skipNext.current = true;     // avoid re-searching the full address we just set
    setSuggestions([]);
    const label = r.short_name || r.display_name;
    setQuery(label);
    await applyPoint(r.lat, r.lon, label);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>{t('offsite.address')}</Label>
        <form onSubmit={search} className="mt-1.5 flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('offsite.addressPlaceholder')}
            data-testid="delivery-address-input"
            className="flex-1"
          />
          <Button type="submit" variant="secondary" disabled={searching} data-testid="delivery-address-search-button" className="shrink-0">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </form>
        {suggestions.length > 0 && (
          <div className="mt-2 rounded-xl border bg-card divide-y" data-testid="delivery-address-suggestions">
            {suggestions.map((r, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pickSuggestion(r)}
                data-testid={`delivery-address-suggestion-${i}`}
                className="flex w-full items-start gap-2 p-2.5 text-left text-sm hover:bg-muted transition-colors"
              >
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                <span className="line-clamp-2">{r.short_name || r.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {geoReady ? (
        <PickerMap
          center={center}
          pin={pin}
          valid={within !== false}
          interactive={false}
          showRadius={false}
          className="h-[240px] md:h-[280px] rounded-[var(--radius)] overflow-hidden border shadow-sm-token"
        />
      ) : (
        <div className="h-[240px] md:h-[280px] rounded-[var(--radius)] border bg-muted/50 flex items-center justify-center text-sm text-muted-foreground" data-testid="delivery-map-loading">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('offsite.locating')}
        </div>
      )}
      {pin && within === true && (
        <div className="flex items-center gap-2 rounded-xl bg-pos-free/15 text-pos-free px-3 py-2 text-sm font-medium" data-testid="delivery-radius-badge">
          <CheckCircle2 className="h-4 w-4" /> {t('offsite.inRadius')}{distance != null ? ` · ${(distance / 1000).toFixed(1)} km` : ''}
        </div>
      )}
      {pin && within === false && (
        <div className="flex items-center gap-2 rounded-xl bg-destructive/10 text-destructive px-3 py-2 text-sm font-medium" data-testid="delivery-radius-badge">
          <XCircle className="h-4 w-4" /> {t('offsite.outOfRadius')}{distance != null ? ` · ${(distance / 1000).toFixed(1)} km` : ''}
        </div>
      )}
    </div>
  );
};
